import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import api from '../api/api';

export default function ClinicManagers({ clinicId, therapists, onSaved }) {
  const [managers, setManagers] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState('');
  const [invite, setInvite] = useState({ first_name: '', last_name: '', email: '' });
  useEffect(() => {
    let active = true;
    api.get('/clinic-managers/', { params: { clinic: clinicId } }).then(({ data }) => {
      if (active) { setManagers(data); setError(''); }
    }).catch(() => { if (active) setError('Could not load clinic managers.'); });
    return () => { active = false; };
  }, [clinicId, refresh]);
  const change = async (action) => {
    if (busy) return;
    setBusy(true); setError('');
    try { await action(); setRefresh((value) => value + 1); onSaved(); }
    catch (err) { setError(err.response?.data?.detail || Object.values(err.response?.data || {}).flat()[0] || 'Could not update clinic managers.'); }
    finally { setBusy(false); }
  };
  const add = (person) => change(() => api.post('/clinic-managers/', {
    clinic_id: clinicId, first_name: person.first_name, last_name: person.last_name, email: person.email,
  }));
  return <>
    <p className="small text-muted">Managers can manage this clinic’s team. This role does not grant staff or clinical access.</p>
    {error && <Alert variant="danger">{error} {!managers && <Button type="button" variant="link" onClick={() => setRefresh((value) => value + 1)}>Retry</Button>}</Alert>}
    {!managers && !error && <Spinner animation="border" role="status"><span className="visually-hidden">Loading clinic managers</span></Spinner>}
    {managers && <fieldset disabled={busy}>
      {!managers.length && <p>No clinic managers assigned.</p>}
      {managers.map((manager) => <div key={manager.id} className="d-flex flex-column flex-sm-row justify-content-between gap-2 border-bottom py-2">
        <div className="text-break"><strong>{manager.name}</strong><div>{manager.email}</div></div>
        <Button type="button" size="sm" variant="outline-danger" className="align-self-start" aria-label={`Remove manager ${manager.email}`} onClick={() => change(() => api.delete(`/clinic-managers/${manager.id}/`))}>Remove</Button>
      </div>)}
      <Form.Group controlId="clinic-manager-therapist" className="mt-3">
        <Form.Label>Add a therapist as manager</Form.Label>
        <Form.Select value={selected} onChange={(event) => setSelected(event.target.value)}>
          <option value="">Select therapist</option>
          {therapists.filter((person) => !managers.some((manager) => manager.email.toLowerCase() === person.email.toLowerCase())).map((person) => <option key={person.id} value={person.id}>{person.first_name} {person.last_name} ({person.email})</option>)}
        </Form.Select>
        <Button type="button" size="sm" className="mt-2" disabled={!selected || busy} onClick={() => { const person = therapists.find((item) => String(item.id) === selected); if (person) add(person); }}>Add manager</Button>
      </Form.Group>
      <details className="mt-3"><summary>Add another manager</summary>
        <p className="small text-muted">Use an existing account’s email, or invite a new non-clinical manager.</p>
        {['first_name', 'last_name', 'email'].map((field) => <Form.Group key={field} controlId={`manager-${field}`} className="mt-2">
          <Form.Label>{field === 'first_name' ? 'Manager first name' : field === 'last_name' ? 'Manager last name' : 'Manager email'}</Form.Label>
          <Form.Control type={field === 'email' ? 'email' : 'text'} maxLength={field === 'email' ? 254 : 30} value={invite[field]} onChange={(event) => setInvite({ ...invite, [field]: event.target.value })} />
        </Form.Group>)}
        <Button type="button" size="sm" className="mt-2" disabled={busy || !invite.first_name.trim() || !invite.last_name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email)} onClick={() => add(invite)}>Add or invite manager</Button>
      </details>
    </fieldset>}
  </>;
}
